import { coordinatesConverter, EpsgCode } from "@geo/js";
import { MGRS } from "@ngageoint/mgrs-js";
// import { IValues } from "@geo/react";
// import { loadModules } from "esri-loader";
// import { getMapViewSpatialReference } from "src/EsriMap";

interface IValues {
    x: string[];
    y: string[];
}

const converterOptions = {
    template: "{y}, {x}", // https://en.wikipedia.org/wiki/ISO_6709
    systems: ["dd", "lks", "ddm", "dms", "mgrs"],
    latlngDesignators: false
};

// const mgrsPattern = /^[0-9]{1,2}[C-HJ-NP-X]{1}[A-HJ-NP-Z]{1}[0-9]{1,5}[0-9]{1,5}$/;

export function trimCoord(coordinate: string, type: string) {
    let newCoordinate = coordinate;
    const coordString = coordinate.split(" ");
    if (type === "dd") {
        const x = parseFloat(coordString[0].replace("N", ""));
        const y = parseFloat(coordString[1].replace("E", ""));
        newCoordinate = x + "°, " + y + "°";
    } else if (type === "ddm") {
        const x = parseFloat(coordString[1].replace("N", ""));
        const y = parseFloat(coordString[3].replace("E", ""));
        newCoordinate = parseInt(coordString[0], 10) + "° " + x + "', " + parseInt(coordString[2], 10) + "° " + y + "'";
    } else if (type === "dms") {
        const x = parseFloat(coordString[2].replace("N", ""));
        const y = parseFloat(coordString[5].replace("E", ""));
        newCoordinate =
            parseInt(coordString[0], 10) +
            "° " +
            coordString[1] +
            "' " +
            // tslint:disable-next-line: quotemark
            x +
            '", ' +
            parseInt(coordString[3], 10) +
            "° " +
            coordString[4] +
            "' " +
            y +
            '"';
    } else if (type === "mgrs") {
        newCoordinate = coordinate;
    }
    return newCoordinate;
}

export interface IInputCoordinates {
    x: (string | number)[];
    y?: (string | number)[];
    format?: coordinateFormat;
}

export const coordToObject = (coordinate: string, type: coordinateFormat): IInputCoordinates => {
    const coordString = coordinate.split(" ");
    if (type === "LKS" || type === "LKSGEO") {
        const x = parseFloat(coordString[0]).toFixed();
        const y = parseFloat(coordString[1]).toFixed();
        return { x: [x], y: [y], format: type };
    } else if (type === "DD") {
        const x = parseFloat(coordString[0].replace("N", ""));
        const y = parseFloat(coordString[1].replace("E", ""));
        return { x: [x], y: [y], format: type };
    } else if (type === "DDM") {
        const x0 = parseFloat(coordString[0]);
        const x1 = parseFloat(coordString[1].replace("N", ""));
        const y0 = parseFloat(coordString[2]);
        const y1 = parseFloat(coordString[3].replace("E", ""));
        return { x: [x0, x1], y: [y0, y1], format: type };
    } else if (type === "DMS") {
        const x0 = parseFloat(coordString[0]);
        const x1 = parseFloat(coordString[1]);
        const x2 = parseFloat(coordString[2].replace("N", ""));
        const y0 = parseFloat(coordString[3]);
        const y1 = parseFloat(coordString[4]);
        const y2 = parseFloat(coordString[5].replace("E", ""));
        return { x: [x0, x1, x2], y: [y0, y1, y2], format: type };
    } else if (type === "MGRS") {
        const x = coordString[0] + coordString[1] + coordString[2] + coordString[3];
        return { x: [x], format: type };
    }
    return;
};

export const convertToString = (format: string, coords: number[]): string => {
    if (!coords || !coords.length || !format) {
        return "";
    }

    if (format === "LKS") {
        return trimCoord(coords[0].toFixed() + ", " + coords[1].toFixed(), format.toLowerCase());
    }
    if (format === "LKSGEO") {
        return trimCoord(coords[1].toFixed() + ", " + coords[0].toFixed(), format.toLowerCase());
    }
    if (format === "DD" || format === "DDM" || format === "DMS") {
        const wgs = coordinatesConverter.convert(coords, EpsgCode.Lks92, EpsgCode.Wgs84);
        const formated = coordinatesConverter.getLatLngCoord(
            { lat: wgs[0], lng: wgs[1] },
            converterOptions,
            format.toLowerCase()
        );
        return trimCoord(typeof formated === "string" ? formated : formated.x + " " + formated.y, format.toLowerCase());
    }
    if (format === "MGRS") {
        const wgs = coordinatesConverter.convert(coords, EpsgCode.Lks92, EpsgCode.Wgs84);
        const mgrs = coordinatesConverter.getLatLngCoord(
            { lat: wgs[1], lng: wgs[0] },
            converterOptions,
            format.toLowerCase()
        );
        return trimCoord(mgrs as string, format.toLowerCase());
    }
    return "";
};

export const convertToPoint = (format: coordinateFormat, coords: IInputCoordinates) => {
    const xy: { x: number | undefined; y: number | undefined } = {
        x: undefined,
        y: undefined
    };
    if (format === "DD") {
        const converted = coordinatesConverter.convert(
            [Number(coords.y[0]), Number(coords.x[0])],
            EpsgCode.Wgs84,
            EpsgCode.Lks92
        );
        xy.x = converted[1];
        xy.y = converted[0];
    }
    if (format === "DDM") {
        const dmFormat = {
            lat: { deg: Number(coords.x[0]), min: Number(coords.x[1]) },
            lng: { deg: Number(coords.y[0]), min: Number(coords.y[1]) }
        };
        const wgs = coordinatesConverter.convertDMtoWGS84(dmFormat);
        const converted = coordinatesConverter.convert([wgs.lng, wgs.lat], EpsgCode.Wgs84, EpsgCode.Lks92);
        xy.x = converted[1];
        xy.y = converted[0];
    }
    if (format === "DMS") {
        const dmsFormat = {
            lat: { deg: Number(coords.x[0]), min: Number(coords.x[1]), sec: Number(coords.x[2]) },
            lng: { deg: Number(coords.y[0]), min: Number(coords.y[1]), sec: Number(coords.y[2]) }
        };
        const wgs = coordinatesConverter.convertDMStoWGS84(dmsFormat);
        const converted = coordinatesConverter.convert([wgs.lng, wgs.lat], EpsgCode.Wgs84, EpsgCode.Lks92);
        xy.x = converted[1];
        xy.y = converted[0];
    }
    if (format === "LKS") {
        xy.x = Number(coords.x[0]);
        xy.y = Number(coords.y[0]);
    }
    if (format === "LKSGEO") {
        xy.x = Number(coords.x[0]);
        xy.y = Number(coords.y[0]);
    }
    if (format === "MGRS" && coords.x.length && coords.x[0].toString().length === 15) {
        try {
            const point = MGRS.parse(coords.x[0].toString()).toPoint();
            const lat = point.getLatitude();
            const lng = point.getLongitude();
            if (!lat || !lng) {
                return "invalidMGRS";
            }

            const converted = coordinatesConverter.convert([lng, lat], EpsgCode.Wgs84, EpsgCode.Lks92);
            xy.x = converted[1];
            xy.y = converted[0];
        } catch {
            return "invalidMGRS";
        }
    }

    return xy.x && xy.y ? xy : null;
};

export type coordinateFormat = "DDM" | "DMS" | "MGRS" | "LKS" | "LKSGEO" | "DD";

export function validateCoords(coords: IInputCoordinates, format: coordinateFormat): boolean {
    let validLength = 1;
    let valid = true;

    if (format === "DDM") {
        validLength = 2;
    } else if (format === "DMS") {
        validLength = 3;
    }
    if (
        (format === "MGRS" && coords.x.length !== validLength) ||
        (format !== "MGRS" && (coords.x.length !== validLength || coords.y.length !== validLength))
    ) {
        valid = false;
    }

    if (valid) {
        const array = format === "MGRS" ? Object.values({ x: coords.x }) : Object.values(coords);
        array.forEach((axis) => {
            axis.forEach((value: string) => {
                if (!value || value === "NaN") {
                    valid = false;
                }
            });
        });
    }
    return valid;
}
